/**
 * useRecordsFilters - Filter state management for records
 *
 * Handles:
 * - Search query (text and semantic)
 * - Channel filter (call/chat/all)
 * - Status filter
 * - Project filter
 * - Advanced filters
 * - Filter panel visibility
 */

import { useEffect, useMemo, useState } from 'react';
import type {
  CallCategory,
  RecordCriterionCondition,
  RecordOutcomeFilter,
} from '@aica/api-types';
import type { RecordItem } from '@/lib/api/mappers';
import { CALL_CATEGORY_OPTIONS, isShortCallRecord } from '@/lib/records/categories';
import {
  DEFAULT_PERSISTED_RECORDS_FILTERS,
  readSavedRecordsFilters,
  writeSavedRecordsFilters,
  type PersistedRecordsFilters,
} from './records-filter-storage';

export type CrmEntityTypeFilter = 'all' | 'deal' | 'lead' | 'contact' | 'company';
export type DirectionFilter = 'all' | 'inbound' | 'outbound' | 'unknown';
/**
 * Проанализирована, но доставка в CRM упала. Прежняя «Проблемні» находила
 * такие записи, а воронка кладёт их в «Проаналізовані» — без отдельной опции
 * их стало бы нечем найти (ревью #1192).
 */
export const DELIVERY_FAILED_FILTER = 'delivery_failed' as const;
/**
 * Строки воронки «Головне» — те же корзины и то же правило, плюс «Інші
 * статуси» (#1108), — и упавшая доставка. Прежние «Проблемні / В обробці /
 * Завершені» с воронкой не совпадали: «В обробці» отправляла ровно
 * `status=pending`, «Проблемні» смешивала сбои обработки с упавшей доставкой.
 */
export type StatusFilter = 'all' | RecordOutcomeFilter | typeof DELIVERY_FAILED_FILTER;
export type AgentRatingMode = 'gte' | 'eq' | 'lte';
export type SentimentFilter = 'all' | 'positive' | 'neutral' | 'negative';

export interface RecordsFilterState {
  searchQuery: string;
  channelFilter: 'all' | 'call' | 'chat';
  directionFilter: DirectionFilter;
  categoryFilter: 'all' | CallCategory;
  projectFilter: string;
  agentFilter: string;
  crmEntityTypeFilter: CrmEntityTypeFilter;
  statusFilter: StatusFilter;
  participantFilter: string;
  activityFilter: string;
  dateFrom: string;
  dateTo: string;
  showFilters: boolean;
  isSemanticSearch: boolean;
  hideMissedCalls: boolean;
  /** The reader's «hide short calls» switch (#876). */
  hideShortCalls: boolean;
  /**
   * What the list is asked for: the switch, unless an outcome or the short
   * category overrides it. What the page shows as applied (chip, counts).
   */
  shortCallsHidden: boolean;
  agentRatingFilter: number;
  agentRatingMode: AgentRatingMode;
  qaScoreMin: number;
  qaScoreMax: number;
  sentimentFilter: SentimentFilter;
  /** Условия по критериям (#865), до трёх, «И». */
  criteriaFilter: RecordCriterionCondition[];
  /** «Шаг скрипта не выполнен» (#879), `<stepId>:missed`; пусто — фильтра нет. */
  scriptStepFilter: string;
}

interface UseRecordsFiltersReturn extends RecordsFilterState {
  // Setters
  setSearchQuery: (query: string) => void;
  setChannelFilter: (filter: 'all' | 'call' | 'chat') => void;
  setDirectionFilter: (filter: DirectionFilter) => void;
  setCategoryFilter: (filter: 'all' | CallCategory) => void;
  setProjectFilter: (filter: string) => void;
  setAgentFilter: (filter: string) => void;
  setCrmEntityTypeFilter: (filter: CrmEntityTypeFilter) => void;
  setStatusFilter: (filter: StatusFilter) => void;
  setParticipantFilter: (filter: string) => void;
  setActivityFilter: (filter: string) => void;
  setDateFrom: (date: string) => void;
  setDateTo: (date: string) => void;
  setShowFilters: (show: boolean) => void;
  setIsSemanticSearch: (enabled: boolean) => void;
  setAgentRatingFilter: (rating: number) => void;
  setAgentRatingMode: (mode: AgentRatingMode) => void;
  setQaScoreMin: (v: number) => void;
  setQaScoreMax: (v: number) => void;
  setSentimentFilter: (v: SentimentFilter) => void;
  setCriteriaFilter: (v: RecordCriterionCondition[]) => void;
  setScriptStepFilter: (v: string) => void;
  toggleFilters: () => void;
  toggleSemanticSearch: () => void;
  toggleHideMissedCalls: () => void;
  setHideShortCalls: (hide: boolean) => void;

  // Filtered data helper
  filterRecords: (records: RecordItem[], semanticResults: RecordItem[]) => RecordItem[];
}

/**
 * Filters a link already names (#1233): the first list request goes out with
 * them, instead of unfiltered and then again once an effect applies them.
 */
export interface RecordsFiltersInitial {
  statusFilter?: StatusFilter;
  channelFilter?: 'call' | 'chat';
  agentFilter?: string;
  /** A category the link names; a value that is not a known one is ignored. */
  categoryFilter?: string;
  /** The direction a link names (#2562); the route has already dropped an unknown value. */
  directionFilter?: Exclude<DirectionFilter, 'all'>;
  agentRatingFilter?: number;
  agentRatingMode?: AgentRatingMode;
  /** `<stepId>:missed` from the link (#879); the route has already dropped a malformed one. */
  scriptStepFilter?: string;
}

/** The link's own values as a set of filters; unset ones stay out of it. */
function linkValues(initial: RecordsFiltersInitial): Partial<PersistedRecordsFilters> {
  const rating =
    initial.agentRatingFilter != null && initial.agentRatingFilter >= 1
      ? initial.agentRatingFilter
      : null;
  const category = (CALL_CATEGORY_OPTIONS as readonly string[]).includes(
    initial.categoryFilter ?? ''
  )
    ? (initial.categoryFilter as CallCategory)
    : null;
  return {
    ...(initial.channelFilter ? { channelFilter: initial.channelFilter } : null),
    ...(initial.statusFilter ? { statusFilter: initial.statusFilter } : null),
    ...(initial.agentFilter ? { agentFilter: initial.agentFilter } : null),
    ...(category ? { categoryFilter: category } : null),
    ...(initial.directionFilter ? { directionFilter: initial.directionFilter } : null),
    ...(rating !== null ? { agentRatingFilter: rating } : null),
    ...(rating !== null && initial.agentRatingMode
      ? { agentRatingMode: initial.agentRatingMode }
      : null),
    ...(initial.scriptStepFilter ? { scriptStepFilter: initial.scriptStepFilter } : null),
  };
}

/**
 * How the filter set outlives the page (#875). The page owns the key — it is
 * built from the user and the organization, which this hook does not know.
 */
export interface RecordsFiltersPersistence {
  /** Null — user or organization not known yet: nothing is read or written. */
  storageKey: string | null;
  /**
   * False when the address carries filters of its own: they replace the saved
   * set whole, never merge with it. True — the address names none (the
   * sidebar, a bare `/records`, a link that only opens one record).
   */
  restore: boolean;
  /**
   * The page holds a link's own state this hook does not keep: a report
   * window, drill-down projects, «scored only», «own only». The list then
   * shows a set the reader never built, so it is not written over the
   * remembered one (returning through the menu would restore half of a link).
   * Writing resumes when that state is gone.
   */
  hold?: boolean;
  /**
   * The projects and agents the page offers, once they are known (null: not
   * loaded yet). A project or agent that came back from the saved set and is
   * not among them any more (deleted, access taken away) is dropped: it would
   * empty the list under a chip that shows a raw id. A list given as null is
   * not known to be complete (the server caps it), so nothing is dropped
   * against it.
   */
  options?: {
    projectIds: readonly string[] | null;
    agentIds: readonly string[] | null;
  } | null;
}

const NOT_PERSISTED: RecordsFiltersPersistence = { storageKey: null, restore: false };

/** The project and agent a saved set put in place: the only ones `options` may drop. */
interface RestoredSelection {
  projectFilter: string;
  agentFilter: string;
}
const NOTHING_RESTORED: RestoredSelection = { projectFilter: 'all', agentFilter: 'all' };
const restoredFrom = (saved: PersistedRecordsFilters | null): RestoredSelection =>
  saved
    ? { projectFilter: saved.projectFilter, agentFilter: saved.agentFilter }
    : NOTHING_RESTORED;

export function useRecordsFilters(
  initial: RecordsFiltersInitial = {},
  persistence: RecordsFiltersPersistence = NOT_PERSISTED
): UseRecordsFiltersReturn {
  const { storageKey, restore, hold = false, options = null } = persistence;
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [isSemanticSearch, setIsSemanticSearch] = useState(false);
  const [criteriaFilter, setCriteriaFilter] = useState<RecordCriterionCondition[]>([]);
  // Switched only by the embedded tab's own control: not part of the remembered set.
  const [hideMissedPreference, setHideMissedPreference] = useState(true);

  // The filters a reader sets and gets back live in one object, so a saved set
  // (or the defaults, after an organization switch) lands in a single update.
  const [values, setValues] = useState<PersistedRecordsFilters>(() => {
    const saved = restore ? readSavedRecordsFilters(storageKey) : null;
    return {
      ...(saved ?? DEFAULT_PERSISTED_RECORDS_FILTERS),
      // What a link names comes with `restore: false`, so it never meets a saved set.
      ...linkValues(initial),
    };
  });
  const [restored, setRestored] = useState<RestoredSelection>(() =>
    restoredFrom(restore ? readSavedRecordsFilters(storageKey) : null)
  );
  const [boundKey, setBoundKey] = useState(storageKey);
  const [restoring, setRestoring] = useState(restore);
  // A link just replaced the set: the page applies the rest of it (window,
  // projects…) in effects after this render, so the next write waits for them.
  const [linkSettling, setLinkSettling] = useState(false);

  // A filtered link arriving while the page stays mounted (`restore` goes from
  // true to false) replaces the remembered set too, not only on mount: without
  // this the link's filter was applied ON TOP of the restored project, dates
  // and switches, and the mixed set got saved (Codex on #1758). Reset during
  // render to the defaults PLUS the link's own values, so the first request
  // after the transition already carries them instead of fetching the
  // unfiltered list for one commit.
  if (restore !== restoring) {
    setRestoring(restore);
    if (!restore) {
      setValues({ ...DEFAULT_PERSISTED_RECORDS_FILTERS, ...linkValues(initial) });
      setRestored(NOTHING_RESTORED);
      setLinkSettling(true);
    }
  }

  // The key becomes known late (the page opened before the role loaded), or
  // changes (another organization, another user). Adjusted during render, not
  // in an effect, so no frame shows — or requests — the previous key's filters.
  // A transient `null` is not a change.
  if (storageKey !== null && storageKey !== boundKey) {
    setBoundKey(storageKey);
    if (boundKey !== null) {
      // Another organization's project or agent must not filter this one.
      const saved = readSavedRecordsFilters(storageKey);
      setValues(saved ?? DEFAULT_PERSISTED_RECORDS_FILTERS);
      setRestored(restoredFrom(saved));
    } else if (restore) {
      const saved = readSavedRecordsFilters(storageKey);
      if (saved) {
        setValues(saved);
        setRestored(restoredFrom(saved));
      }
    }
  }

  useEffect(() => {
    if (linkSettling) setLinkSettling(false);
  }, [linkSettling]);

  useEffect(() => {
    if (storageKey !== null && storageKey === boundKey && !hold && !linkSettling) {
      writeSavedRecordsFilters(storageKey, values);
    }
  }, [storageKey, boundKey, values, hold, linkSettling]);

  // Once the offered projects and agents are known, what the saved set brought
  // back that is not among them goes. A project or agent the reader picked
  // since, or a link named, is not the saved set's to drop.
  useEffect(() => {
    if (!options) return;
    setValues((prev) => {
      const dropProject =
        prev.projectFilter !== 'all' &&
        prev.projectFilter === restored.projectFilter &&
        options.projectIds !== null &&
        !options.projectIds.includes(prev.projectFilter);
      const dropAgent =
        prev.agentFilter !== 'all' &&
        prev.agentFilter === restored.agentFilter &&
        options.agentIds !== null &&
        !options.agentIds.includes(prev.agentFilter);
      if (!dropProject && !dropAgent) return prev;
      return {
        ...prev,
        ...(dropProject ? { projectFilter: 'all' } : null),
        ...(dropAgent ? { agentFilter: 'all' } : null),
      };
    });
  }, [options, restored]);

  const setters = useMemo(() => {
    const set =
      <K extends keyof PersistedRecordsFilters>(key: K) =>
      (value: PersistedRecordsFilters[K]) =>
        setValues((prev) => (prev[key] === value ? prev : { ...prev, [key]: value }));
    return {
      setChannelFilter: set('channelFilter'),
      setDirectionFilter: set('directionFilter'),
      setCategoryFilter: set('categoryFilter'),
      setProjectFilter: set('projectFilter'),
      setAgentFilter: set('agentFilter'),
      setCrmEntityTypeFilter: set('crmEntityTypeFilter'),
      setStatusFilter: set('statusFilter'),
      setParticipantFilter: set('participantFilter'),
      setActivityFilter: set('activityFilter'),
      setDateFrom: set('dateFrom'),
      setDateTo: set('dateTo'),
      setHideShortCalls: set('hideShortCalls'),
      setAgentRatingFilter: set('agentRatingFilter'),
      setAgentRatingMode: set('agentRatingMode'),
      setQaScoreMin: set('qaScoreMin'),
      setQaScoreMax: set('qaScoreMax'),
      setSentimentFilter: set('sentimentFilter'),
      setScriptStepFilter: set('scriptStepFilter'),
    };
  }, []);

  const {
    channelFilter,
    directionFilter,
    categoryFilter,
    projectFilter,
    agentFilter,
    crmEntityTypeFilter,
    statusFilter,
    participantFilter,
    activityFilter,
    dateFrom,
    dateTo,
    hideShortCalls,
    agentRatingFilter,
    agentRatingMode,
    qaScoreMin,
    qaScoreMax,
    sentimentFilter,
    scriptStepFilter,
  } = values;

  // An outcome shows missed calls too: the funnel counts them, and on prod
  // 89 % of «no recording» are missed calls (4 175 of 4 678, week to 27.09) —
  // hidden, the list would show a tenth of the funnel's number. Derived, not
  // written into the preference: the web page has no hide/show toggle to
  // turn it back, so clearing the outcome must restore the list by itself
  // (#1108).
  const hideMissedCalls =
    hideMissedPreference &&
    (statusFilter === 'all' || statusFilter === DELIVERY_FAILED_FILTER);
  // The same for short calls (#876), and for the same reason: the funnel files
  // short-flagged rows under other outcomes too (no recording, failed…), so
  // any outcome, not only «too short», and the «short» category ask for calls
  // the switch hides, and the list would fall short of the funnel's own
  // count. `hideShortCalls` is the reader's switch, `shortCallsHidden` what
  // the list is asked for.
  const shortCallsHidden =
    hideShortCalls &&
    (statusFilter === 'all' || statusFilter === DELIVERY_FAILED_FILTER) &&
    categoryFilter !== 'short';

  const toggleFilters = () => setShowFilters((prev) => !prev);
  const toggleSemanticSearch = () => setIsSemanticSearch((prev) => !prev);
  const toggleHideMissedCalls = () => setHideMissedPreference((prev) => !prev);

  // Filter records — most filters are now server-side.
  // Only participant/activity text search remains client-side
  // (covered by server 'search' param but useful for instant filtering within loaded results).
  const filterRecords = useMemo(() => {
    return (records: RecordItem[], semanticResults: RecordItem[]): RecordItem[] => {
      const semantic = isSemanticSearch && Boolean(searchQuery);
      // The semantic search is not sent `excludeShort` (an older edge-api
      // rejects the unknown filter): its one page is cut here, by the same
      // rule the list's server applies.
      const sourceRecords = semantic
        ? shortCallsHidden
          ? semanticResults.filter((record) => !isShortCallRecord(record))
          : semanticResults
        : records;

      // All major filters (channel, direction, category, project, agent, crmEntityType,
      // dateFrom/dateTo, excludeMissed, agentRating) are handled server-side via API query params.
      // Client-side: participant/activity text search only.
      if (!participantFilter && !activityFilter) {
        return sourceRecords;
      }

      return sourceRecords.filter((record) => {
        if (participantFilter) {
          const query = participantFilter.toLowerCase();
          const matchesName = record.participantName?.toLowerCase().includes(query);
          const matchesPhone = record.participantPhone?.toLowerCase().includes(query);
          const matchesEmail = record.participantEmail?.toLowerCase().includes(query);
          if (!matchesName && !matchesPhone && !matchesEmail) {
            return false;
          }
        }

        if (activityFilter) {
          const query = activityFilter.toLowerCase();
          if (!record.crmActivityName?.toLowerCase().includes(query)) {
            return false;
          }
        }

        return true;
      });
    };
  }, [
    isSemanticSearch,
    searchQuery,
    participantFilter,
    activityFilter,
    shortCallsHidden,
  ]);

  return {
    // State
    searchQuery,
    channelFilter,
    directionFilter,
    categoryFilter,
    projectFilter,
    agentFilter,
    crmEntityTypeFilter,
    statusFilter,
    participantFilter,
    activityFilter,
    dateFrom,
    dateTo,
    showFilters,
    isSemanticSearch,
    hideMissedCalls,
    hideShortCalls,
    shortCallsHidden,
    agentRatingFilter,
    agentRatingMode,
    qaScoreMin,
    qaScoreMax,
    sentimentFilter,
    criteriaFilter,
    scriptStepFilter,

    // Setters
    setSearchQuery,
    ...setters,
    setShowFilters,
    setIsSemanticSearch,
    setCriteriaFilter,
    toggleFilters,
    toggleSemanticSearch,
    toggleHideMissedCalls,

    // Helper
    filterRecords,
  };
}
